import { tokens } from "../../../theme";
import {
  Box,
  Button,
  Chip,
  InputAdornment,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { InfoOutlined, Search } from "@mui/icons-material";
import FilterMultiSelect from "../../../components/FilterMultiSelect";
import { releaseOptionLabel } from "../summaryHelpers";
import { releaseStyles } from "../styles";

const SEGMENTS = [
  { value: "all", label: "All", tone: "neutral" },
  { value: "double", label: "Double", tone: "double" },
  { value: "original", label: "Original", tone: "neutral" },
  { value: "noTicket", label: "No ticket", tone: "muted" },
];

const ServiceFilterBar = ({
  serviceOptions,
  releaseOptions,
  patchOptions,
  filters,
  onFilterChange,
  onClearAll,
  searchInput,
  onSearchChange,
  counts,
  allExpanded,
  onToggleAll,
}) => {
  const theme = useTheme();
  const { releaseView } = releaseStyles(tokens(theme.paletteId));

  const groups = [
    {
      name: "services",
      label: "Services",
      options: serviceOptions,
      itemsNoun: "services",
    },
    {
      name: "releases",
      label: "Releases",
      options: releaseOptions,
      itemsNoun: "releases",
      getOptionLabel: releaseOptionLabel,
    },
    {
      name: "patches",
      label: "Patches",
      options: patchOptions,
      itemsNoun: "patches",
    },
  ];

  const applied = groups.flatMap(({ name, getOptionLabel }) =>
    (filters[name] || []).map((value) => ({
      name,
      value,
      text: getOptionLabel?.(value) ?? value,
    }))
  );
  const hasActiveFilters =
    applied.length > 0 || Boolean(searchInput) || filters.segment !== "all";

  return (
    <>
      <Box sx={releaseView.filterBar}>
        <Box sx={releaseView.filterGroup}>
          {groups.map(({ name, label, options, itemsNoun, getOptionLabel }) => (
            <FilterMultiSelect
              key={name}
              label={label}
              options={options}
              itemsNoun={itemsNoun}
              getOptionLabel={getOptionLabel}
              selected={filters[name]}
              onChange={(value) => onFilterChange(name, value)}
              sx={releaseView.filterButton}
            />
          ))}

          <TextField
            size="small"
            placeholder="Search message, ticket, service, or hash"
            value={searchInput}
            sx={releaseView.searchField}
            onChange={(event) => onSearchChange(event.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <Box sx={releaseView.filterActions}>
          <Tooltip
            arrow
            placement="top"
            componentsProps={{
              tooltip: { sx: releaseView.segmentTooltip },
            }}
            title={
              <Box>
                <Box component="span" sx={releaseView.segmentTooltipRow}>
                  Narrow the commit rows below to one kind:
                </Box>
                <Box component="span" sx={releaseView.segmentTooltipRow}>
                  <b>All</b> — every commit in scope.
                </Box>
                <Box component="span" sx={releaseView.segmentTooltipRow}>
                  <b>Double</b> — commits carried over from an earlier release
                  (forward-ported downstream).
                </Box>
                <Box component="span" sx={releaseView.segmentTooltipRow}>
                  <b>Original</b> — commits written for this branch, not carried
                  over.
                </Box>
                <Box component="span" sx={releaseView.segmentTooltipRow}>
                  <b>No ticket</b> — commits with no Jira ID attached.
                </Box>
              </Box>
            }
          >
            <Box component="span" sx={releaseView.segmentLabelWrap}>
              <InfoOutlined sx={releaseView.segmentInfoIcon} />
            </Box>
          </Tooltip>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={filters.segment}
            sx={releaseView.segmentGroup}
            onChange={(_, segment) =>
              segment && onFilterChange("segment", segment)
            }
          >
            {SEGMENTS.map((segment) => (
              <ToggleButton
                key={segment.value}
                value={segment.value}
                sx={releaseView.segmentButton(segment.tone)}
              >
                {segment.label} {counts[segment.value] ?? ""}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>

          <Button
            size="small"
            variant="outlined"
            sx={releaseView.expandButton}
            onClick={onToggleAll}
          >
            {allExpanded ? "Collapse all" : "Expand all"}
          </Button>
        </Box>
      </Box>

      {hasActiveFilters ? (
        <Box sx={releaseView.appliedFiltersRow}>
          <Typography sx={releaseView.appliedFiltersLabel}>
            FILTERING BY
          </Typography>
          {applied.map(({ name, value, text }) => (
            <Chip
              key={`${name}-${value}`}
              label={text}
              size="small"
              sx={releaseView.appliedFilterChip}
              onDelete={() =>
                onFilterChange(
                  name,
                  filters[name].filter((item) => item !== value)
                )
              }
            />
          ))}
          {searchInput ? (
            <Chip
              label={`search: ${searchInput}`}
              size="small"
              sx={releaseView.appliedFilterChip}
              onDelete={() => onSearchChange("")}
            />
          ) : null}
          {filters.segment !== "all" ? (
            <Chip
              label={SEGMENTS.find((s) => s.value === filters.segment)?.label}
              size="small"
              sx={releaseView.appliedFilterChip}
              onDelete={() => onFilterChange("segment", "all")}
            />
          ) : null}
          <Button sx={releaseView.clearAllButton} onClick={onClearAll}>
            Clear all
          </Button>
        </Box>
      ) : null}
    </>
  );
};

export default ServiceFilterBar;
